import { PopulationSamples } from '../shared/model/population';
import {
  MAX_BREAK,
  PROFILE_SEXES,
  bandPopulations,
  everyoneProfile,
  regrid,
} from './profile.model';

/**
 * Generational cohorts (Pew Research Center cut points) and an area's
 * residents across them. A generation is just a fixed age band, so this reuses
 * {@link bandPopulations} to split census buckets that straddle a boundary.
 */
export interface Generation {
  id: string;
  label: string;
  /** First birth year, inclusive. */
  bornFrom: number;
  /** Last birth year, inclusive. */
  bornTo: number;
}

/**
 * Youngest first. Gen Alpha and the Silent Generation are left out but stay in
 * the population the shares are taken of, so shares do not add to 100%.
 */
export const GENERATIONS: readonly Generation[] = [
  { id: 'gen-z', label: 'Gen Z', bornFrom: 1997, bornTo: 2012 },
  { id: 'millennials', label: 'Millennials', bornFrom: 1981, bornTo: 1996 },
  { id: 'gen-x', label: 'Gen X', bornFrom: 1965, bornTo: 1980 },
  { id: 'boomers', label: 'Boomers', bornFrom: 1946, bornTo: 1964 },
];

export interface GenerationSlice extends Generation {
  /** The cohort's ages in the data year, e.g. "11–26". */
  ageLabel: string;
  people: number;
  /** Share of the area's population, 0..1. */
  share: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Half-open [from, to) age band in `dataYear`. Keyed off the data vintage, not
 * today: census age is age at the survey.
 */
export function generationAgeBand(
  generation: Generation,
  dataYear: number
): [number, number] {
  const from = clamp(dataYear - generation.bornTo, 0, MAX_BREAK - 1);
  const to = clamp(dataYear - generation.bornFrom + 1, from + 1, MAX_BREAK);
  return [from, to];
}

/** Inclusive label ("11–26"), since generations are disjoint. */
export function generationAgeLabel(from: number, to: number): string {
  return to >= MAX_BREAK ? `${from}+` : `${from}–${to - 1}`;
}

/**
 * @param populationTotal the host's authoritative population. The ACS5 age-sex
 *   table does not sum to it, so when given, the table decides only shares and
 *   counts are scaled to this total.
 * @returns one slice per {@link GENERATIONS} entry, or [] when nobody lives here.
 */
export function generationBreakdown(
  samples: PopulationSamples,
  dataYear: number,
  populationTotal: number | null = null
): GenerationSlice[] {
  const bands = GENERATIONS.map((g) => generationAgeBand(g, dataYear));
  const breaks = [...new Set([0, ...bands.flat(), MAX_BREAK])].sort(
    (a, b) => a - b
  );

  const perSex = bandPopulations(regrid(everyoneProfile(), breaks), samples);
  const perBand = breaks
    .slice(0, -1)
    .map((_, band) =>
      PROFILE_SEXES.reduce((sum, sex) => sum + perSex[sex][band], 0)
    );

  const total = perBand.reduce((sum, people) => sum + people, 0);
  if (total <= 0) return [];

  return GENERATIONS.map((generation, i) => {
    const [from, to] = bands[i];
    // Summed over the grid, not read at one index: clamping can merge bands
    // for a data year far outside ACS's range.
    let people = 0;
    for (let band = 0; band < perBand.length; band++) {
      if (breaks[band] >= from && breaks[band + 1] <= to) {
        people += perBand[band];
      }
    }

    const share = people / total;

    return {
      ...generation,
      ageLabel: generationAgeLabel(from, to),
      people:
        populationTotal != null && populationTotal > 0
          ? share * populationTotal
          : people,
      share,
    };
  });
}
